import { createContext, useContext, useReducer, useEffect } from 'react'
import { supabaseAuthAPI } from '../services/supabaseAuth'
import { auth } from '../lib/supabase'
import { usersAPI } from '../services/api'
import toast from 'react-hot-toast'

const AuthContext = createContext()

const initialState = {
  user: null,
  token: localStorage.getItem('supabase_token'),
  loading: true,
  isAuthenticated: false,
  session: null,
}

function authReducer(state, action) {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload }
    case 'LOGIN_SUCCESS':
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        session: action.payload.session,
        isAuthenticated: true,
        loading: false,
      }
    case 'LOGOUT':
      return {
        ...state,
        user: null,
        token: null,
        session: null,
        isAuthenticated: false,
        loading: false,
      }
    case 'UPDATE_USER':
      return {
        ...state,
        user: { ...state.user, ...action.payload },
      }
    default:
      return state
  }
}

export function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(authReducer, initialState)

  // Function to fetch user profile and merge with Supabase user
  const fetchUserProfile = async (supabaseUser, token) => {
    try {
      // Set token in localStorage so API calls can use it
      localStorage.setItem('supabase_token', token)
      
      // Try to fetch user profile from backend
      const profileResponse = await usersAPI.getById(supabaseUser.id)
      const userProfile = profileResponse.data.user
      
      // Merge Supabase user with backend profile
      const mergedUser = {
        ...supabaseUser,
        ...userProfile,
        role: userProfile.role || 'user', // Default to 'user' if no role specified
      }
      
      return mergedUser
    } catch (error) {
      console.error('Failed to fetch user profile:', error)
      // If profile fetch fails, check if user email indicates admin
      const isAdmin = supabaseUser.email === 'admin@zovalabs.com' || 
                     supabaseUser.email === 'admin@globalaichallenge.com' ||
                     supabaseUser.email === 'admin@gloabalaichallenge.com'
      
      const fallbackUser = {
        ...supabaseUser,
        role: isAdmin ? 'admin' : 'user'
      }
      
      return fallbackUser
    }
  }

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const { data, error } = await auth.getCurrentSession()
        
        if (error) {
          throw error
        }

        if (data.session) {
          const userWithProfile = await fetchUserProfile(data.session.user, data.session.access_token)
          
          dispatch({
            type: 'LOGIN_SUCCESS',
            payload: {
              user: userWithProfile,
              token: data.session.access_token,
              session: data.session,
            },
          })
        } else {
          dispatch({ type: 'SET_LOADING', payload: false })
        }
      } catch (error) {
        console.error('Auth initialization error:', error)
        localStorage.removeItem('supabase_token')
        localStorage.removeItem('user')
        dispatch({ type: 'LOGOUT' })
      }
    }

    initializeAuth()

    // Listen for auth changes
    const { data: { subscription } } = auth.onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_IN' && session) {
        const userWithProfile = await fetchUserProfile(session.user, session.access_token)
        
        dispatch({
          type: 'LOGIN_SUCCESS',
          payload: {
            user: userWithProfile,
            token: session.access_token,
            session: session,
          },
        })
      } else if (event === 'SIGNED_OUT') {
        dispatch({ type: 'LOGOUT' })
      }
    })

    return () => subscription.unsubscribe()
  }, [])

  const login = async (email, password) => {
    try {
      const response = await supabaseAuthAPI.login(email, password)
      const { token, user } = response.data

      // The auth state change listener will handle the dispatch
      toast.success('Login successful!')
      return { success: true }
    } catch (error) {
      const message = error.message || 'Login failed'
      toast.error(message)
      return { success: false, error: message }
    }
  }

  const resetPassword = async (email) => {
    try {
      const response = await supabaseAuthAPI.resetPassword(email)
      toast.success(response.data.message || 'Password reset email sent!')
      return { success: true }
    } catch (error) {
      const message = error.message || 'Password reset failed'
      toast.error(message)
      return { success: false, error: message }
    }
  }

  const register = async (userData) => {
    try {
      const response = await supabaseAuthAPI.register(userData)
      
      toast.success(response.data.message || 'Registration successful!')
      return { success: true }
    } catch (error) {
      const message = error.message || 'Registration failed'
      toast.error(message)
      return { success: false, error: message }
    }
  }

  const logout = async () => {
    try {
      await supabaseAuthAPI.logout()
      // The auth state change listener will handle the dispatch
      toast.success('Logged out successfully')
    } catch (error) {
      console.error('Logout error:', error)
      // Force logout even if there's an error
      dispatch({ type: 'LOGOUT' })
      toast.success('Logged out successfully')
    }
  }

  const updateProfile = async (updates) => {
    try {
      const response = await supabaseAuthAPI.updateProfile(updates)
      dispatch({
        type: 'UPDATE_USER',
        payload: response.data.user,
      })
      toast.success('Profile updated successfully!')
      return { success: true }
    } catch (error) {
      const message = error.message || 'Profile update failed'
      toast.error(message)
      return { success: false, error: message }
    }
  }

  const value = {
    ...state,
    login,
    register,
    resetPassword,
    logout,
    updateProfile,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}